import { Container, Grid } from '~/components/layout/PageStructure';
import React, { useContext, useEffect, useState } from 'react';
import CollectionFilters from '~/components/collection/CollectionFilters';
import CollectionSorting from '~/components/collection/CollectionSorting';
import CollectionStructuredData from '~/components/meta/structured-data/CollectionStructuredData';
import FilterContext from '~/context/FilterContext';
import PageTitle from '~/components/modules/pageTitle';
import Paginator from '~/components/ui/Paginator';
import ProductGrid from '~/components/product/ProductGrid';
import PropTypes from 'prop-types';
import SEO from '~/components/meta/SEO';
import SearchStructuredData from '~/components/meta/structured-data/SearchStructuredData';
import UIContext from '~/context/UIContext';
import client from '~/utils/client';
import { productFragment } from '~/utils/queries';
import useParams from '~/hooks/useParams';
import enums from '~/utils/enums';
import cn from 'classnames';
import useStoreConfig from '~/hooks/useStoreConfig';
import { CollectionPageLoader } from '~/components/ui/molecules/skeleton-loaders/CollectionPageLoader';
import useRestClient from '~/hooks/useRestClient';
import { ProductFilterLoader } from '~/components/ui/molecules/skeleton-loaders/ProductFilterLoader';
import Skeleton from 'react-loading-skeleton';

const itemsPerPage = 24;
const productsQuery = `
  paginatorInfo {
    count
    total
    currentPage
    lastPage
  }
  data {
    ${productFragment}
  }
`;

const additionalOptions = {
  inStockOnly: {
    label: 'Stock',
    values: ['In stock'],
    type: 'Boolean',
  },
};

const addAdditionalOptions = filterOptions => {
  let reducedAddlOptions = {};
  Object.keys(additionalOptions).forEach(categoryKey => {
    const category = additionalOptions[categoryKey];
    reducedAddlOptions = {
      ...reducedAddlOptions,
      [categoryKey]: category,
    };
  });
  return {
    ...filterOptions,
    ...reducedAddlOptions,
  };
};

const ProductGridWithFilters = ({
  variables,
  queryFilters,
  productsFilters,
  requestType,
  collectionSlug = '',
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [collection, setCollection] = useState(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Default values are not set to wait for URL context
  const [activeFilters, setActiveFilters] = useState();
  const [page, setPage] = useState();
  const [sortMethod, setSortMethod] = useState('');
  const defaultSortMethod = enums.sortMethods.featured;
  const storeConfig = useStoreConfig();

  let showFilters = true;
  let showSortOptions = true;
  if (storeConfig?.collection) {
    showFilters = storeConfig.collection.showFilters;
    showSortOptions = storeConfig.collection.showSortOptions;
  }

  const { breakpoint } = useContext(UIContext);
  const { getParam, deleteParam, setParam } = useParams();

  useEffect(() => {
    // If there are filters set, the page and sort method have received their initial value, and there are products
    // Products are required to ensure we don't update the page on load
    if (
      Object.keys(activeFilters || {}).length &&
      page &&
      sortMethod &&
      products.length
    ) {
      setPage(1);
    }
    /**
     * eslint disable is required here because we only want to update on filter change
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters]);

  const restClient = useRestClient();

  useEffect(() => {
    if (!activeFilters || isFilterLoading) {
      return;
    }
    const fetchData = async () => {
      setIsFilterLoading(true);
      const res = await restClient
        .getFilterOptions({ ...activeFilters, slug: collectionSlug })
        .finally(() => {
          setIsFilterLoading(false);
        });
      setFilterOptions(res.data.data.filterOptions);
      setCollection(res.data.data.collection);
    };

    void fetchData();
  }, [
    restClient,
    setFilterOptions,
    setCollection,
    collectionSlug,
    activeFilters,
  ]);

  useEffect(() => {
    // Wait for initial page and filters object from URL context
    if (page === undefined || activeFilters === undefined) return;

    /**
     * Map through selected options and ensure they are of the correct type by using additionalOptions (manually added options)
     * selectedFilterMutations - an array with the types of the values. Type can be specified manually via additionalOptions key
     * selectedFilterParameters - an array to be used as a reference to use the variables set in selectedFilterMutations
     */
    const selectedFilterKeys = Object.keys(activeFilters);

    let selectedFilterMutations = [];
    let selectedFilterParameters = [];

    for (let i = 0; i < selectedFilterKeys.length; i++) {
      const filterKey = selectedFilterKeys[i];
      const filterType = additionalOptions[filterKey]?.type || '[String!]';

      selectedFilterMutations.push(`$${filterKey}: ${filterType}!`);
      selectedFilterParameters.push(`${filterKey}: $${filterKey}`);
    }

    selectedFilterMutations = selectedFilterMutations.join(', ');
    selectedFilterParameters = selectedFilterParameters.join(', ');

    let contentQuery = `
      products(${productsFilters}, ${selectedFilterParameters}) {
        ${productsQuery}
      }
    `;

    if (requestType === 'collectionPage') {
      contentQuery = `
        collection(slug: $slug) {
          ${contentQuery}
        }
      `;
    }

    setIsLoading(true);
    client
      .request(
        `
        query(${queryFilters}, ${selectedFilterMutations}) {
          ${contentQuery}
        }`,
        {
          order: sortMethod || defaultSortMethod,
          page,
          itemsPerPage,
          slug: variables.slug || null,
          query: variables.query || null,
          ...activeFilters,
        },
        { cache: true },
      )
      .then(response => {
        const responseContent = response?.collection || response || [];
        setProducts(responseContent?.products?.data || []);
        setPaginationData(responseContent?.products?.paginatorInfo || {});
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortMethod,
    page,
    activeFilters,
    breakpoint.isTabletOrSmaller,
    variables.slug,
    variables.query,
  ]);

  let {
    title = '',
    seoTitle = '',
    seoDescription = '',
    contentHTML = '',
  } = collection || {};
  const { count = 0, total = 0 } = paginationData;

  if (requestType === 'searchPage') {
    title = isLoading
      ? `Searching for "${variables.query}"...`
      : `Your search for "${variables.query}" found ${total} items`;
  }

  return (
    <FilterContext
      getParam={getParam}
      deleteParam={deleteParam}
      setParam={setParam}
      setFilters={setActiveFilters}
      setSortMethod={setSortMethod}
    >
      <Container className="mb-8">
        <SEO title={seoTitle ? seoTitle : title} description={seoDescription} />
        {isFilterLoading ? (
          <Skeleton
            style={{ width: '30%' }}
            className={'py-1 my-7 grid m-auto'}
          />
        ) : (
          <PageTitle title={title} />
        )}

        <div className="grid grid-cols-5 gap-6">
          {showFilters && !breakpoint.isTabletOrSmaller && (
            <ProductFilterLoader
              isReady={!isFilterLoading}
              className={'col-span-1 hidden md:block mt-4'}
            >
              <CollectionFilters
                sidebarClassName="col-span-1 hidden md:block mt-4"
                toolbarClassName="hidden"
                onFilterUpdate={setActiveFilters}
                filters={addAdditionalOptions(filterOptions)}
              />
            </ProductFilterLoader>
          )}

          <div
            className={cn('col-span-full', {
              'md:col-span-4': showFilters,
            })}
          >
            <div className="grid grid-cols-2 gap-4 mb-4 lg:grid-cols-3">
              {showFilters && breakpoint.isTabletOrSmaller ? (
                <ProductFilterLoader
                  isReady={!isFilterLoading}
                  className={
                    'mb-2 sm:mb-0 block md:hidden col-span-1 col-start-1 w-full'
                  }
                >
                  <CollectionFilters
                    sidebarClassName="hidden"
                    toolbarClassName="mb-2 sm:mb-0 block md:hidden col-span-1 col-start-1 w-full"
                    onFilterUpdate={setActiveFilters}
                    filters={addAdditionalOptions(filterOptions)}
                  />
                </ProductFilterLoader>
              ) : (
                <span className="self-end text-grey text-tiny lg:text-tiny-lg">
                  Showing {count} of {total} items
                </span>
              )}

              {/* This can be shown while loading on larger screens */}
              {showSortOptions &&
                (!isFilterLoading || !breakpoint.isTabletOrSmaller) && (
                  <CollectionSorting
                    sortMethod={sortMethod}
                    sortMethodCallback={null}
                    selectClassName="col-span-1 col-start-2 lg:col-start-3 md:w-max md:ml-auto"
                  />
                )}
            </div>

            <Grid>
              <CollectionPageLoader totalThumbs={15} ready={!isLoading}>
                <ProductGrid
                  products={products}
                  productClassName="col-span-2 md:col-span-4 lg:col-span-3 mb-3"
                />
              </CollectionPageLoader>
            </Grid>

            {!isLoading && (
              <>
                <Paginator
                  pageContext={paginationData}
                  onPageUpdate={setPage}
                  activePage={page}
                />
                {contentHTML && (
                  <div className="product-grid--collection-description grid grid-cols-3 border-t-4 border-main-1 mt-4 pt-4 text-small">
                    <div
                      className="col-span-3 md:col-span-2"
                      dangerouslySetInnerHTML={{ __html: contentHTML }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {paginationData?.currentPage && (
          <>
            {requestType === 'collectionPage' && (
              <CollectionStructuredData
                title={title}
                location={window.location}
                page={paginationData?.currentPage}
              />
            )}
            {requestType === 'searchPage' && (
              <SearchStructuredData url={window.location.href} title={title} />
            )}
          </>
        )}
      </Container>
    </FilterContext>
  );
};

ProductGridWithFilters.propTypes = {
  query: PropTypes.string.isRequired,
  variables: PropTypes.object.isRequired,
  queryFilters: PropTypes.string.isRequired,
  productsFilters: PropTypes.string.isRequired,
  requestType: PropTypes.string.isRequired,
  collectionSlug: PropTypes.string,
};

export default React.memo(ProductGridWithFilters);
